/**
 * Lot routing business rules for Native Interactive Site Map (ISM).
 *
 * Pure functions — no Redux, no DOM. Decides which view (if any) a lot click should navigate to,
 * given the lot's own data and device context. Consumed by
 * redux/selectors.js#selectLotRoutingOutcome, the single hub for the full navigation decision
 * (destination, or unwind, or true no-op) — this module only resolves the "destination" half.
 *
 * @module nativeInteractiveSiteMap/services/lotRoutingService
 */
import LotStatus from '../enums/lotStatus';
import { 
  VIEW_LOT_MAP_SELECTION, 
  VIEW_FLOOR_PLAN_DETAIL, 
  VIEW_HOUSING_UNIT_DETAIL, 
  VIEW_ENTRY_SOURCE_MAP_LOT 
} from '../constants';

/**
 * Views resolveLotDestination can itself produce. Consumed by
 * redux/selectors.js#selectLotRoutingOutcome to recognize "we're inside a view THIS function
 * routed us into", so a lot click that no longer qualifies knows to unwind rather than leave
 * that view showing stale data for the wrong lot (see NHS20-8166).
 */
export const LOT_ROUTABLE_VIEWS = [VIEW_LOT_MAP_SELECTION, VIEW_FLOOR_PLAN_DETAIL, VIEW_HOUSING_UNIT_DETAIL];

/**
 * Resolves the destination view for a lot click, based on the lot's status, plan count, whether
 * it has a housing unit built on it, whether it matches the currently active chip, and device. A
 * lot can be Available/Model with >1 plan and still not qualify if the user is browsing a
 * different chip (e.g. Sold) — those lots are greyed-out background decoration on the map (see
 * components/lotPolygonColors.dom.js#applyFilter) and clicking one must not jump the user into
 * this flow.
 *
 * Eligible statuses: Available and Model (NHS20-8238 requires covering QMI, Model Home, and
 * Floor Plan matches) — Sold and Coming Soon are handled by their own destinations elsewhere
 * (or not yet, per NHS20-8167). Called for every matched lot click regardless of status; a Sold
 * or Coming Soon lot still reaches it, it just fails isEligibleStatus and returns null
 * immediately, before any of the four outcomes below.
 *
 * Four outcomes:
 *  - housePlanIds.length > 1: VIEW_LOT_MAP_SELECTION, both desktop and mobile. In practice only
 *    reachable for Available lots — a Model lot is an already-built home (housingUnitId set),
 *    never a multi-plan buildable one (see the mutually-exclusive housePlanIds/housingUnitId
 *    note on services/processCommunityMap.js).
 *  - lot.housingUnitId is set: mobile routes to VIEW_LOT_MAP_SELECTION (a preview step, per
 *    NHS20-8238); desktop routes straight to VIEW_HOUSING_UNIT_DETAIL
 *    (plugins/housingUnitDetail.plugin.js).
 *  - housePlanIds.length === 1: mobile routes to VIEW_LOT_MAP_SELECTION (same preview step);
 *    desktop routes straight to VIEW_FLOOR_PLAN_DETAIL (plugins/floorPlanDetail.plugin.js).
 *  - anything else: null (no destination) — each new destination plugin extends this function
 *    with its own resolution rule rather than owning a separate routing decision.
 *
 * @param {object[]} lots
 * @param {{ sitePlanLotIds: Set<string> }} matchedLotIds
 * @param {string} sitePlanLotId - the SVG click id (see libraries/mapLibrary/svgLotInteraction.js);
 *   named distinctly from lot.lotId (the integer field) because both are compared in this
 *   function — using the same name for both would risk matching the wrong one silently.
 * @param {boolean} isMobile - device flag; determines whether a single-result lot resolves to
 *   the lotMapSelection preview (mobile) or straight to its detail view (desktop).
 * @returns {{ view: string, context: { planId: string|null, selectedLotId: string|null,
 *   housingUnitId: number|null } } | null}
 */
export function resolveLotDestination(lots, matchedLotIds, sitePlanLotId, isMobile) {
  const lot = lots.find((candidate) => candidate.sitePlanLotId === sitePlanLotId);
  const isEligibleStatus = lot?.status === LotStatus.Available.toString() ||
    lot?.status === LotStatus.Model.toString();

  if (!lot || !isEligibleStatus || !matchedLotIds.sitePlanLotIds.has(sitePlanLotId)) {
    return null;
  }

  if (lot.housePlanIds.length > 1) {
    return {
      view: VIEW_LOT_MAP_SELECTION,
      context: { planId: null, selectedLotId: sitePlanLotId, housingUnitId: null },
    };
  }

  if (lot.housingUnitId != null) {
    if (isMobile) {
      return {
        view: VIEW_LOT_MAP_SELECTION,
        context: { planId: null, selectedLotId: sitePlanLotId, housingUnitId: null },
      };
    }
    return {
      view: VIEW_HOUSING_UNIT_DETAIL,
      context: { 
        planId: null, 
        selectedLotId: sitePlanLotId, 
        housingUnitId: lot.housingUnitId, 
        source: VIEW_ENTRY_SOURCE_MAP_LOT 
      },
    };
  }

  if (lot.housePlanIds.length === 1) {
    if (isMobile) {
      return {
        view: VIEW_LOT_MAP_SELECTION,
        context: { planId: null, selectedLotId: sitePlanLotId, housingUnitId: null },
      };
    }
    return {
      view: VIEW_FLOOR_PLAN_DETAIL,
      context: { 
        planId: lot.housePlanIds[0], 
        selectedLotId: sitePlanLotId, 
        housingUnitId: null, 
        source: VIEW_ENTRY_SOURCE_MAP_LOT 
      },
    };
  }

  return null;
}
